
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share, Plus, X } from "lucide-react";
import { isIOS, isSafari } from "@/utils/device";
import logoImage from "@/assets/logo.png";

// Store both vote time and team URL
interface VoteData {
  timestamp: number;
  teamName?: string;
  teamUrl?: string;
}

// PWA installation helper functions
const pwaHelpers = {
  // Check if the app is already installed as a PWA
  isPWAInstalled: () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
          (window.navigator as any).standalone === true;
  },

  // Check if user has recently voted (used as a trigger condition)
  hasRecentlyVoted: () => {
    const lastVoteDataStr = localStorage.getItem('lastVoteData');
    if (!lastVoteDataStr) return false;
    
    try {
      const lastVoteData: VoteData = JSON.parse(lastVoteDataStr);
      // Voting within the last 60 seconds counts as "recent"
      const timeElapsed = Date.now() - lastVoteData.timestamp;
      return timeElapsed < 60 * 1000; // 60 seconds
    } catch (error) {
      console.error("Error parsing vote data:", error);
      return false;
    }
  },

  // Get the current team data from localStorage if available
  getTeamData: () => {
    const lastVoteDataStr = localStorage.getItem('lastVoteData');
    if (!lastVoteDataStr) return { teamName: '', teamUrl: '' };
    
    try {
      const lastVoteData: VoteData = JSON.parse(lastVoteDataStr);
      return {
        teamName: lastVoteData.teamName || '',
        teamUrl: lastVoteData.teamUrl || ''
      };
    } catch (error) {
      console.error("Error parsing team data:", error);
      return { teamName: '', teamUrl: '' };
    }
  },

  // Save that we've shown the prompt to prevent repeated prompts
  markPromptShown: () => {
    localStorage.setItem('pwaPromptShown', 'true');
    // Set expiration to 30 days
    const expirationDate = Date.now() + (30 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwaPromptShownExpires', expirationDate.toString());
  },

  // Check if we should show the prompt or not
  shouldShowPrompt: () => {
    // Temporary disable auto-showing of prompt
    return false;
    
    // The code below is disabled until we find a more reliable way to show the prompt
    /*
    // If already installed as PWA, don't show
    if (pwaHelpers.isPWAInstalled()) return false;
    
    // Only show on iOS Safari
    if (!isIOS() || !isSafari()) return false;
    
    // Check if prompt was already shown
    const promptShown = localStorage.getItem('pwaPromptShown');
    if (promptShown === 'true') {
      // Check if the "don't show again" period has expired
      const expirationStr = localStorage.getItem('pwaPromptShownExpires');
      if (expirationStr) {
        const expiration = parseInt(expirationStr);
        if (Date.now() < expiration) {
          return false; // Still within the don't show period
        }
      }
    }
    
    return pwaHelpers.hasRecentlyVoted();
    */
  }
};

// Record vote time and team info - exported for use in other components
export const recordVoteTime = (teamName?: string, teamUrl?: string) => {
  const voteData: VoteData = {
    timestamp: Date.now(),
    teamName,
    teamUrl: teamUrl || window.location.href
  };
  
  localStorage.setItem('lastVoteData', JSON.stringify(voteData));
};

// The installation instructions component - iOS specific
const InstallationInstructions = () => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center p-3 rounded-lg">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <Share className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm">Tap the <strong>Share</strong> button</p>
        </div>
      </div>
      
      <div className="flex items-center justify-center p-3 rounded-lg">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <Plus className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm">Then tap <strong>Add to Home Screen</strong></p>
        </div>
      </div>
      
      <div className="flex justify-center">
        <img 
          src={logoImage} 
          alt="Vote App Icon"
          className="h-12 w-12 rounded-lg"
        />
      </div>
    </div>
  );
};

export function PWAInstallPrompt() {
  const [open, setOpen] = useState(false);
  const { teamName, teamUrl } = pwaHelpers.getTeamData();
  const appTitle = teamName ? `${teamName} Vote` : 'Vote.GameOn';

  useEffect(() => {
    // Temporary disabled - prompt will not show automatically
    // Only check on iOS Safari and after voting
    if (pwaHelpers.shouldShowPrompt()) {
      // Show after a short delay to ensure the page has fully loaded
      // and user has seen the vote success message
      const timer = setTimeout(() => {
        setOpen(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    pwaHelpers.markPromptShown();
  };

  // Use the team URL if available, otherwise use the home page
  const startUrl = teamUrl || "/";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[350px]">
        <DialogHeader>
          <div className="flex items-center justify-center mb-1">
            <img 
              src={logoImage}
              alt="Vote.GameOn Logo" 
              className="h-10 w-10"
            />
          </div>
          <DialogTitle className="text-center text-lg">Add {appTitle} to Home</DialogTitle>
          <DialogDescription className="text-center text-sm">
            For quick access to team voting
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-3">
          <InstallationInstructions />
          
          <div className="flex justify-end pt-2">
            <Button onClick={handleClose} variant="outline" className="gap-1 text-sm" size="sm">
              <X className="h-3 w-3" />
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
